import { printDollars } from "shared/utils/dollar-print";

export const NO_VERIFIED_INCOME_LABEL = "No Verified Income";
export const MISSING_VERIFIED_INCOME_LABEL = "—";

export const roundHalfUpToTwoDecimals = (amount: number): number =>
  Math.round(amount * 100) / 100;

const formatMonthlyDollarsWithSuffix = (monthlyDollars: number): string =>
  `${printDollars(roundHalfUpToTwoDecimals(monthlyDollars))}/mo`;

export const formatDealGroupVerifiedLine = (
  totalVerifiedIncomeCents: number | null | undefined
): string => {
  if (totalVerifiedIncomeCents === null || totalVerifiedIncomeCents === undefined) {
    return `Total Group Verified: ${MISSING_VERIFIED_INCOME_LABEL}`;
  }

  if (totalVerifiedIncomeCents === 0) {
    return `Total Group Verified: ${NO_VERIFIED_INCOME_LABEL}`;
  }

  const monthlyDollars = totalVerifiedIncomeCents / 100 / 12;
  return `Total Group Verified: ${formatMonthlyDollarsWithSuffix(monthlyDollars)}`;
};

export const formatDealGroupTargetLine = (
  totalVerifiedIncomeCents: number | null | undefined,
  incomeToRentRatio: number | null | undefined,
  idealIncomeRentRatio: number | null | undefined,
  monthlyRentDollars?: number | null | undefined
): string => {
  if (
    idealIncomeRentRatio === null ||
    idealIncomeRentRatio === undefined ||
    idealIncomeRentRatio === 0
  ) {
    return `Total Group Target: ${MISSING_VERIFIED_INCOME_LABEL}`;
  }

  if (monthlyRentDollars != null && monthlyRentDollars > 0) {
    return `Total Group Target: ${formatMonthlyDollarsWithSuffix(
      monthlyRentDollars * idealIncomeRentRatio
    )}`;
  }

  if (totalVerifiedIncomeCents === 0) {
    return `Total Group Target: ${NO_VERIFIED_INCOME_LABEL}`;
  }

  if (
    totalVerifiedIncomeCents != null &&
    totalVerifiedIncomeCents > 0 &&
    incomeToRentRatio != null &&
    incomeToRentRatio > 0
  ) {
    const monthlyVerifiedDollars = totalVerifiedIncomeCents / 100 / 12;
    const monthlyTargetDollars =
      (monthlyVerifiedDollars / incomeToRentRatio) * idealIncomeRentRatio;

    return `Total Group Target: ${formatMonthlyDollarsWithSuffix(
      monthlyTargetDollars
    )}`;
  }

  return `Total Group Target: ${
    totalVerifiedIncomeCents === 0
      ? NO_VERIFIED_INCOME_LABEL
      : MISSING_VERIFIED_INCOME_LABEL
  }`;
};

/**
 * Deal-level PRODUCT-1486 rows shown directly under the income-to-rent ratio.
 */
export const getDealGroupIncomeLines = (
  totalVerifiedIncomeCents: number | null | undefined,
  incomeToRentRatio: number | null | undefined,
  idealIncomeRentRatio: number | null | undefined,
  enableMonthlyIncomeDisplay: boolean,
  monthlyRentDollars?: number | null | undefined
): string[] => {
  if (!enableMonthlyIncomeDisplay) {
    return [];
  }

  return [
    formatDealGroupVerifiedLine(totalVerifiedIncomeCents),
    formatDealGroupTargetLine(
      totalVerifiedIncomeCents,
      incomeToRentRatio,
      idealIncomeRentRatio,
      monthlyRentDollars
    ),
  ];
};

export const formatVerifiedAnnualIncomeDisplay = (
  verifiedIncomeCents: number | null | undefined,
  isSubmitted: boolean,
  enableMonthlyIncomeDisplay: boolean,
  legacyFormatter: (
    income: number | null | undefined,
    type: "Income" | "Balance",
    isSubmitted: boolean
  ) => string
): string => {
  const annualDollars =
    verifiedIncomeCents != null ? verifiedIncomeCents * 0.01 : null;

  if (!enableMonthlyIncomeDisplay) {
    return legacyFormatter(annualDollars, "Income", isSubmitted);
  }

  // Match legacy formatter: only show Pending when unsubmitted and income isn't available yet.
  if (!isSubmitted && (annualDollars === null || annualDollars === 0)) {
    return "Pending";
  }

  if (annualDollars === null) {
    return MISSING_VERIFIED_INCOME_LABEL;
  }

  if (annualDollars === 0) {
    return NO_VERIFIED_INCOME_LABEL;
  }

  return printDollars(annualDollars);
};

export const formatVerifiedMonthlyIncomeDisplay = (
  verifiedIncomeCents: number | null | undefined,
  isSubmitted: boolean,
  enableMonthlyIncomeDisplay: boolean
): string | null => {
  if (!enableMonthlyIncomeDisplay) {
    return null;
  }

  if (verifiedIncomeCents === null || verifiedIncomeCents === undefined) {
    return !isSubmitted ? "Pending" : MISSING_VERIFIED_INCOME_LABEL;
  }

  const annualDollars = verifiedIncomeCents * 0.01;

  if (annualDollars === 0) {
    return !isSubmitted ? "Pending" : NO_VERIFIED_INCOME_LABEL;
  }

  return printDollars(roundHalfUpToTwoDecimals(annualDollars / 12));
};
